import {useState, useCallback} from "react";

type UseControlledProps<T> = {
  /**
   * The component value when it's controlled.
   */
  controlled?: T;
  /**
   * The default value when it's uncontrolled.
   */
  default?: T;
};
/**
 * Hook to help manage controlled and uncontrolled state, an important
 * requirement for foundational components.
 * For more information on this pattern, reference https://reactjs.org/docs/glossary.html#controlled-vs-uncontrolled-components
 */
export function useControlled<T>({
  controlled,
  default: defaultProp,
}: UseControlledProps<T>): [T, (newValue: T) => void] {
  const isControlled = controlled !== undefined;
  const [valueState, setValue] = useState(defaultProp);
  const value = (isControlled ? controlled : valueState) as T;

  const setValueIfUncontrolled = useCallback(
    (newValue) => {
      if (!isControlled) {
        setValue(newValue);
      }
    },
    [isControlled]
  );

  return [value, setValueIfUncontrolled];
}
